const _ = require('lodash');
const Intl = require('intl');
require('intl/locale-data/jsonp/zh.js');
require('intl/locale-data/jsonp/hr.js');
require('intl/locale-data/jsonp/nl.js');
require('intl/locale-data/jsonp/en.js');
require('intl/locale-data/jsonp/fr.js');
require('intl/locale-data/jsonp/de.js');
require('intl/locale-data/jsonp/it.js');
require('intl/locale-data/jsonp/pt.js');
require('intl/locale-data/jsonp/ro.js');
require('intl/locale-data/jsonp/es.js');
require('intl/locale-data/jsonp/bg.js');
require('intl/locale-data/jsonp/cs.js');
require('intl/locale-data/jsonp/el.js');
require('intl/locale-data/jsonp/hu.js');
require('intl/locale-data/jsonp/sl.js');
require('intl/locale-data/jsonp/lt.js');
require('intl/locale-data/jsonp/lv.js');
require('intl/locale-data/jsonp/pl.js');
require('intl/locale-data/jsonp/tr.js');
require('intl/locale-data/jsonp/ru.js');
require('intl/locale-data/jsonp/sq.js');
require('intl/locale-data/jsonp/bs.js');
require('intl/locale-data/jsonp/mk.js');
require('intl/locale-data/jsonp/sr.js');
require('intl/locale-data/jsonp/sk.js');
require('intl/locale-data/jsonp/sv.js');
require('intl/locale-data/jsonp/uk.js');
require('intl/locale-data/jsonp/nb.js');
require('intl/locale-data/jsonp/ko.js');
require('intl/locale-data/jsonp/vi.js');
require('intl/locale-data/jsonp/zh-Hant-TW.js');
require('intl/locale-data/jsonp/fi.js');
require('intl/locale-data/jsonp/da.js');
require('intl/locale-data/jsonp/th.js');
require('intl/locale-data/jsonp/ca-ES.js');

const Currency = require('./currency');
const Symbol = require('./symbol');
const XE = require('./xe');

const DEFAULT_LANGUAGE = 'en';

class Money {
  constructor (money) {
    if (!money) return;

    this.amount = money.amount;
    this.currency = money.currency;
    this.language = money.language || DEFAULT_LANGUAGE;
    this.symbol = Symbol[this.currency];
  }

  isValid () {
    return _.isFinite(this.amount)
    && _.isString(this.currency)
    && _.isString(this.symbol); // if currency is valid then symbol exists
  }

  add (other) {

    if (!(other instanceof Money)) throw new Error('other should be an instance of Money');

    if (!_.eq(this.currency, other.currency))
      other = other.convertTo(this.currency);

    return new Money({
      amount: this.amount + other.amount,
      currency: this.currency,
      language: this.language
    });
  }

  subtract (other) {

    if (!(other instanceof Money)) throw new Error('other should be an instance of Money');

    if (!_.eq(this.currency, other.currency))
      other = other.convertTo(this.currency);

    return new Money({
      amount: this.amount - other.amount,
      currency: this.currency,
      language: this.language
    });
  }

  multiply (factor) {
    return new Money({
      amount: this.amount * factor,
      currency: this.currency,
      language: this.language
    });
  }

  divide (factor) {
    return new Money({
      amount: this.amount / factor,
      currency: this.currency,
      language: this.language
    });
  }

  convertTo (currency) {

    if (!currency)
      throw new Error('currency is required');

    if (!_.has(Currency, currency))
      throw new Error('currency is unsupported');

    if (_.eq(this.currency, currency))
      return this;

    // When zero (if currency is undefined):
    if (this.isZero()) {
      return new Money({
        amount: 0,
        currency: currency,
        language: this.language
      });
    } else {

      if (!this.currency)
        throw new Error('Money does not have defined its currency');

      const conversionRate = XE[this.currency][currency];

      if (!conversionRate)
        throw new Error('Conversion rate not found');

      return new Money({
        amount: this.amount * conversionRate,
        currency: currency,
        language: this.language
      });
    }
  }

  isZero () {
    return _.eq(this.amount, 0);
  }

  isPositive () {
    return _.gt(this.amount, 0);
  }

  isNegative () {
    return _.lt(this.amount, 0);
  }

  isPositiveOrZero () {
    return _.gte(this.amount, 0);
  }

  isNegativeOrZero () {
    return _.lte(this.amount, 0);
  }

  isEqual (other) {

    if (!(other instanceof Money)) throw new Error('other should be an instance of Money');

    if (!_.eq(this.currency, other.currency))
      other = other.convertTo(this.currency);

    return _.isEqual(_.pick(this, 'amount', 'currency'), _.pick(other, 'amount', 'currency'));
  }

  isLessThan (other) {

    if (!(other instanceof Money)) throw new Error('other should be an instance of Money');

    if (!_.eq(this.currency, other.currency))
      other = other.convertTo(this.currency);

    return _.lt(this.amount, other.amount);
  }

  isGreaterThan (other) {

    if (!(other instanceof Money)) throw new Error('other should be an instance of Money');

    if (!_.eq(this.currency, other.currency))
      other = other.convertTo(this.currency);

    return _.gt(this.amount, other.amount);
  }

  isLessThanOrEqual (other) {

    if (!(other instanceof Money)) throw new Error('other should be an instance of Money');

    if (!_.eq(this.currency, other.currency))
      other = other.convertTo(this.currency);

    return _.lte(this.amount, other.amount);
  }

  isGreaterThanOrEqual (other) {

    if (!(other instanceof Money)) throw new Error('other should be an instance of Money');

    if (!_.eq(this.currency, other.currency))
      other = other.convertTo(this.currency);

    return _.gte(this.amount, other.amount);
  }

  round (precision) {
    return new Money({
      amount: _.round(this.amount, precision),
      currency: this.currency,
      language: this.language
    });
  }

  ceil (ceil) {
    return new Money({
      amount: Math.ceil((this.amount) / ceil) * ceil,
      currency: this.currency,
      language: this.language
    });
  }

  locale (language) {
    return new Money({
      amount: this.amount,
      currency: this.currency,
      language: language
    });
  }

  value () {
    return {
      currency: this.currency,
      amount: Math.round(this.amount)
    };
  }

  toString () {
    return [Math.round(this.amount), this.currency].join(' ');
  }

  format (params) {

    const defaults = {
      currencyDisplay: 'symbol', // 'symbol' (€) , 'code' (EUR), 'none' (ø)
      minimumFractionDigits: 0
    };

    params = _.defaults(params, defaults);

    return params.currencyDisplay !== 'none'

      ? new Intl
        .NumberFormat(this.language, _.assign({}, params, {currency: this.currency, style: 'currency'}))
        .format(this.amount / 100)

      : new Intl
        .NumberFormat(this.language, _.assign({}, _.omit(params, 'currencyDisplay'), {currency: this.currency, style: 'decimal', maximumFractionDigits: 2}))
        .format(this.amount / 100);

  }
}

module.exports = function () {

  if (arguments.length === 2) {

    return new Money({amount: arguments[0], currency: arguments[1]});

  } else if (arguments.length === 1) {

    let input = arguments[0];

    if (_.isPlainObject(input) || _.isObject(input))
      return new Money(input);

    if (_.isString(input)) {

      input = input.replace(/\s/g, '').replace(/,/g, '.');

      const result = input.match(/^([A-Z]{3})?(\d*\.?\d*)([A-Z]{3})?$/);

      return result && (result[1] || result[3])
        ? new Money({amount: parseFloat(result[2]) * 100, currency: result[1] || result[3]})
        : new Money();
    }

    if (_.isFinite(input))
      return new Money({amount: input});

    if (_.isNil(input))
      return new Money();

  } else if (arguments.length === 0) {
    return new Money();
  }
};

module.exports.min = function () {

  let moneys = _(arguments)
    .toArray()
    .flattenDeep()
    .compact()
    .value();

  if (_.isEmpty(moneys)) return;

  const areValid = _.every(moneys, function (it) {
    return it.constructor === Money && it.isValid();
  });

  if (!areValid) throw new Error('arguments should be valid money');

  const baseCurrency = moneys[0].currency;
  const baseLanguage = moneys[0].language;

  moneys = _.map(moneys, function (it) {
    return it.convertTo(baseCurrency);
  });

  return new Money({
    amount: _.minBy(moneys, 'amount').amount,
    currency: baseCurrency,
    language: baseLanguage
  });
};

module.exports.max = function () {

  let moneys = _(arguments)
    .toArray()
    .flattenDeep()
    .compact()
    .value();

  if (_.isEmpty(moneys)) return;

  const areValid = _.every(moneys, function (it) {
    return it.constructor === Money && it.isValid();
  });

  if (!areValid) throw new Error('arguments should be valid money');

  const baseCurrency = moneys[0].currency;
  const baseLanguage = moneys[0].language;

  moneys = _.map(moneys, function (it) {
    return it.convertTo(baseCurrency);
  });

  return new Money({
    amount: _.maxBy(moneys, 'amount').amount,
    currency: baseCurrency,
    language: baseLanguage
  });
};

module.exports.Currency = Currency;
module.exports.Symbol =  Symbol;
module.exports.XE =  XE;
